import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Images, Mountain } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { ClimbRecentPhotos } from './ClimbRecentPhotos';
import { ClimbRecentScans } from './ClimbRecentScans';
import type { RecentClimbPhoto } from '../../../types/climb';
import type { RecentClimbScan } from '../../../types/climb-scan';

/** A sector whose photo can stand in as the climb's. */
export interface ZonePhoto {
  id: string;
  name: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
}

interface ClimbPhotoSourceProps {
  onFile: (file: File) => void;
  onZonePhoto: (zone: ZonePhoto) => void;
  /**
   * The location's sectors that have a photo. Empty hides that way in — which is
   * what a gym sends: see the note on the component.
   */
  zones: ZonePhoto[];
  /** The wall photos this place's last climbs were drawn on. Empty hides them. */
  recent: RecentClimbPhoto[];
  onRecentPhoto: (photo: RecentClimbPhoto) => void;
  /**
   * The walls this place has had scanned. Empty hides them — which is what
   * anywhere but a gym gets, and anybody but its staff.
   */
  scans?: RecentClimbScan[];
  onScan?: (scan: RecentClimbScan) => void;
  /** Whether a scan's holds come with it: a boulder is chosen from them. */
  scansOfferHolds?: boolean;
  /** Draw as a full block (no photo yet) or as a dialog opened from the overlay. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * What the dialog is headed. "Change the photo" by default, which is what the
   * overlay button opens it for — a climb that has none is *given* one instead,
   * and is asked in those words (see ClimbRow).
   */
  title?: string;
  /**
   * A way past this screen without a picture. Only where a climb may have none —
   * on rock, where a line is often recorded from a topo with no photo to hand,
   * and for a gym's admin entering a wall as a list. Without it the two icons
   * would be a wall rather than a doorway.
   */
  onSkip?: () => void;
  /**
   * What going on without a picture means here — and, by being given at all, that
   * it is worth stopping to say so: the skip then asks for confirmation, with this
   * as the warning. Left out, the skip goes straight through, which is what rock
   * wants (a route recorded off a topo has no photo and needs no ceremony).
   *
   * Indoors it is the other way round. The picture is not being given up, only
   * postponed onto whoever climbs the thing, and a wall entered as a list is a
   * wall of cameras — so the one who would rather not be asked is exactly the one
   * who should be.
   */
  skipWarning?: string;
}

/**
 * How a photo gets here: the camera, the gallery, and — depending on the place —
 * a picture it already has.
 *
 * The two buttons are the decision. What sits under them is whichever shortcut
 * the place can offer, and the two places offer different ones:
 *
 * * **A crag** offers its **sectors**. A sector *is* the picture a route is drawn
 *   on, the same cliff shot serving every line on it, so a setter picks the
 *   sector rather than hunting for that photo in their camera roll each time.
 *   Choosing one also says which sector the climb is in, which is a field the
 *   form then does not have to ask for.
 * * **A gym** offers its **last walls** to its own staff (`ClimbRecentPhotos`).
 *   Indoors a sector is a name for a corner of the room and its photo, when
 *   there is one at all, is a wide shot of the whole thing — no use to draw a
 *   problem on. What is of use is the wall somebody photographed an hour ago,
 *   because a reset is a session: one shot, six problems. That is why the sector
 *   way in is not offered indoors (`PhotoClimbEditor` sends no zones there) —
 *   it was a list of pictures nobody was going to draw on.
 *
 *   And above those, the walls it has had **scanned** (`ClimbRecentScans`): the
 *   same kind of picture, plus every hold the scan found on it, which a boulder
 *   is then chosen from instead of drawn. A scanned wall is offered there and
 *   only there — the last walls leave it out (see the controller), so the one
 *   picture does not sit in two rows behaving two different ways.
 *
 * Rendered inline while there is no photo, and as a dialog once there is one and
 * the overlay button asks to change it.
 */
export function ClimbPhotoSource({
  onFile,
  onZonePhoto,
  zones,
  recent,
  onRecentPhoto,
  scans = [],
  onScan,
  scansOfferHolds = false,
  open,
  onOpenChange,
  title,
  onSkip,
  skipWarning,
}: ClimbPhotoSourceProps) {
  const { t } = useTranslation();
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  // The sectors are a second step rather than a row of the first: a crag has
  // more of them than any one row has space for.
  const [showZones, setShowZones] = useState(false);
  // Whether going on without a picture is being asked about — see `skipWarning`.
  const [confirmingSkip, setConfirmingSkip] = useState(false);

  const take = (file: File | undefined) => {
    if (!file) return;
    onFile(file);
    onOpenChange?.(false);
    setShowZones(false);
  };

  const pickRecent = (photo: RecentClimbPhoto) => {
    onRecentPhoto(photo);
    onOpenChange?.(false);
  };

  const pickScan = (scan: RecentClimbScan) => {
    onScan?.(scan);
    onOpenChange?.(false);
  };

  const buttons = (
    <div className="space-y-3">
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => take(event.target.files?.[0])}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => take(event.target.files?.[0])}
      />

      {showZones ? (
        <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto">
          {zones.map((zone) => (
            <button
              key={zone.id}
              type="button"
              onClick={() => {
                onZonePhoto(zone);
                onOpenChange?.(false);
                setShowZones(false);
              }}
              className="overflow-hidden rounded-md border text-left transition-colors hover:border-primary"
            >
              <img
                src={zone.thumbnailUrl || zone.imageUrl}
                alt=""
                className="aspect-video w-full object-cover"
              />
              <span className="block truncate px-2 py-1 text-xs">{zone.name}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Two ways in, as big as the decision they are: this is the whole of
              the page until there is a photo, because every other field is about
              a climb that cannot be drawn yet. */}
          <div className="flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={() => cameraInput.current?.click()}
              aria-label={t('climbEditor.photo.camera')}
              className="flex size-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Camera className="size-10" aria-hidden />
              <span className="text-xs">{t('climbEditor.photo.camera')}</span>
            </button>
            <button
              type="button"
              onClick={() => galleryInput.current?.click()}
              aria-label={t('climbEditor.photo.gallery')}
              className="flex size-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Images className="size-10" aria-hidden />
              <span className="text-xs">{t('climbEditor.photo.gallery')}</span>
            </button>
          </div>

          {/* The walls this gym has just been shot on. Under the two buttons
              rather than beside them: it is a shortcut for the common case, not
              a third way in. The scanned ones first: a setter putting up a
              reset photographed it to be scanned, and picked from there a wall
              comes with its holds. */}
          {onScan && (
            <ClimbRecentScans scans={scans} onPick={pickScan} offersHolds={scansOfferHolds} />
          )}
          <ClimbRecentPhotos photos={recent} onPick={pickRecent} />

          {/* Two more ways on, kept quiet. A sector's photo is the usual source
              outdoors, but it is a list rather than a decision; and going on
              without a picture at all is a choice rock offers everybody and a
              gym offers only whoever runs it. */}
          <div className="flex flex-col items-center">
            {zones.length > 0 && (
              <Button type="button" variant="link" className="gap-2" onClick={() => setShowZones(true)}>
                <Mountain className="size-4" aria-hidden />
                {t('climbEditor.photo.sector')}
              </Button>
            )}
            {onSkip && (
              /* Quiet on purpose: grey, small, and no underline until it is
                 pointed at. It is a way out for whoever needs it, not one of the
                 two ways in above — and where it is worth discouraging it also
                 has the dialog to get through (see `skipWarning`). */
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-xs font-normal text-muted-foreground hover:text-foreground"
                onClick={() => (skipWarning ? setConfirmingSkip(true) : onSkip())}
                data-testid="climb-photo-skip"
              >
                {t('climbEditor.photo.skip')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  /**
   * Going on without a picture, asked about rather than done.
   *
   * The weight is deliberately the wrong way round for the thing being confirmed:
   * backing out is the solid button and carrying on is the quiet one. Nobody is
   * stopped — an admin entering a wall as a list has a good reason and gets there
   * in one more press — but the reason is put to them first.
   */
  const skipConfirm = onSkip && skipWarning && (
    <Dialog open={confirmingSkip} onOpenChange={setConfirmingSkip}>
      <DialogContent className="sm:max-w-sm" data-testid="climb-photo-skip-confirm">
        <DialogHeader>
          <DialogTitle>{t('climbEditor.photo.skipTitle')}</DialogTitle>
          <DialogDescription>{skipWarning}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Button type="button" onClick={() => setConfirmingSkip(false)}>
            {t('climbEditor.photo.skipBack')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            data-testid="climb-photo-skip-confirmed"
            onClick={() => {
              setConfirmingSkip(false);
              onSkip();
            }}
          >
            {t('climbEditor.photo.skipAnyway')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (open === undefined) {
    return (
      <div className="space-y-4 px-4 py-10 text-center">
        <p className="text-sm text-muted-foreground">{t('climbEditor.photo.intro')}</p>
        {buttons}
        {skipConfirm}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange?.(next);
        if (!next) setShowZones(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? t('climbEditor.photo.change')}</DialogTitle>
          <DialogDescription>{t('climbEditor.photo.intro')}</DialogDescription>
        </DialogHeader>
        {buttons}
      </DialogContent>
      {skipConfirm}
    </Dialog>
  );
}
